import { useEffect, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown } from "lucide-react";
import { NAV, PRIMARY_CTA, CONSULTATION_CTA, BRAND_LOGO_NAVY, CBE_BADGE } from "@/lib/data";

// Shorter display for the longest nav item (no-op now that "Operations & Analytics" is removed)
const displayLabel = (label) => label;

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");

// Desktop nav item with optional dropdown
const DesktopNavItem = ({ item }) => {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;
  const closeTimer = useRef(null);

  const isActiveParent = hasChildren
    ? item.children.some((c) => location.pathname === c.to) || location.pathname === item.to
    : false;

  const handleEnter = () => {
    if (closeTimer.current) clearTimeout(closeTimer.current);
    setOpen(true);
  };
  const handleLeave = () => {
    closeTimer.current = setTimeout(() => setOpen(false), 120);
  };

  if (!hasChildren) {
    return (
      <NavLink
        to={item.to}
        end={item.to === "/"}
        data-testid={`nav-link-${slug(item.label)}`}
        className={({ isActive }) => `nav-link whitespace-nowrap ${isActive ? "is-active" : ""}`}
      >
        {displayLabel(item.label)}
      </NavLink>
    );
  }

  return (
    <div className="relative" onMouseEnter={handleEnter} onMouseLeave={handleLeave}>
      <NavLink
        to={item.to}
        data-testid={`nav-link-${slug(item.label)}`}
        className={({ isActive }) =>
          `nav-link whitespace-nowrap inline-flex items-center gap-1.5 ${isActive || isActiveParent ? "is-active" : ""}`
        }
      >
        {displayLabel(item.label)}
        <ChevronDown
          size={12}
          strokeWidth={2.5}
          className={`transition-transform ${open ? "rotate-180" : ""}`}
        />
      </NavLink>

      <div
        className={`absolute left-0 top-full pt-3 min-w-[220px] transition-all duration-200 ${
          open ? "opacity-100 visible translate-y-0" : "opacity-0 invisible -translate-y-1"
        }`}
        data-testid={`nav-dropdown-${slug(item.label)}`}
      >
        <div className="bg-white border border-gray-200 shadow-lg py-2">
          {item.children.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              data-testid={`nav-dropdown-link-${slug(child.label)}`}
              className={({ isActive }) =>
                `block px-5 py-3 text-[13px] tracking-[0.06em] transition-colors ${
                  isActive
                    ? "text-[#000000] bg-[#F8F9FA] font-medium"
                    : "text-gray-700 hover:bg-[#F8F9FA] hover:text-[#000000]"
                }`
              }
              onClick={() => setOpen(false)}
            >
              {child.label}
            </NavLink>
          ))}
        </div>
      </div>
    </div>
  );
};

// Mobile nav item with optional inline children
const MobileNavItem = ({ item, onClose }) => {
  const [open, setOpen] = useState(false);
  const hasChildren = Array.isArray(item.children) && item.children.length > 0;

  if (!hasChildren) {
    return (
      <NavLink
        to={item.to}
        end={item.to === "/"}
        onClick={onClose}
        data-testid={`mobile-nav-link-${slug(item.label)}`}
        className={({ isActive }) =>
          `py-2.5 border-b border-gray-100 text-[15px] block ${isActive ? "text-[#000000] font-medium" : "text-gray-700"}`
        }
      >
        {item.label}
      </NavLink>
    );
  }

  return (
    <div className="border-b border-gray-100">
      <button
        type="button"
        onClick={() => setOpen((s) => !s)}
        data-testid={`mobile-nav-toggle-${slug(item.label)}`}
        className="w-full py-2.5 flex items-center justify-between text-[15px] text-gray-700"
      >
        <span>{item.label}</span>
        <ChevronDown size={16} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="pb-2 pl-3 border-l border-[#C5A880] ml-1 mb-1">
          {item.children.map((child) => (
            <NavLink
              key={child.to}
              to={child.to}
              onClick={onClose}
              data-testid={`mobile-nav-link-${slug(child.label)}`}
              className={({ isActive }) =>
                `block py-2 text-[14px] ${isActive ? "text-[#000000] font-medium" : "text-gray-600"}`
              }
            >
              {child.label}
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
};

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(() => typeof window !== "undefined" && window.scrollY > 8);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const closeMenu = () => setOpen(false);

  return (
    <header
      data-testid="site-navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-xl border-b border-gray-100" : "bg-white"
      }`}
    >
      <div className="container-kc flex items-center justify-between h-20 gap-6">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-3 sm:gap-4 group shrink-0">
          <img src={BRAND_LOGO_NAVY} alt="Kassim Consulting" className="h-11 w-auto transition-opacity group-hover:opacity-80" />
          <span className="h-8 w-px bg-gray-200 hidden sm:block" aria-hidden="true" />
          <img
            src={CBE_BADGE}
            alt="DC Certified Business Enterprise"
            title="DC Certified Business Enterprise"
            className="h-9 sm:h-10 w-auto object-contain transition-opacity group-hover:opacity-80"
            data-testid="navbar-cbe-badge"
          />
        </Link>

        <nav className="hidden lg:flex items-center gap-7 xl:gap-9" data-testid="primary-nav">
          {NAV.map((item) => (
            <DesktopNavItem key={item.label} item={item} />
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3 shrink-0">
          <Link
            to={CONSULTATION_CTA.to}
            data-testid="nav-cta-free-consultation"
            className="inline-flex items-center px-3.5 py-2 border border-[#C5A880] text-[#000000] hover:bg-[#C5A880] hover:text-white text-[11px] tracking-[0.16em] uppercase font-semibold transition-colors whitespace-nowrap"
          >
            {CONSULTATION_CTA.label}
          </Link>
          <Link
            to={PRIMARY_CTA.to}
            data-testid="nav-cta-contact"
            className="inline-flex items-center px-4 py-2 bg-[#000000] text-white hover:bg-[#C5A880] text-[11px] tracking-[0.16em] uppercase font-semibold transition-colors whitespace-nowrap"
          >
            {PRIMARY_CTA.label}
            <span className="ml-2">→</span>
          </Link>
        </div>

        <button
          data-testid="mobile-menu-toggle"
          onClick={() => setOpen((s) => !s)}
          className="lg:hidden p-2 -mr-2 text-[#000000]"
          aria-label="Toggle menu"
        >
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden border-t border-gray-100 bg-white max-h-[calc(100vh-80px)] overflow-y-auto" data-testid="mobile-nav">
          <div className="container-kc py-5 flex flex-col">
            {NAV.map((item) => (
              <MobileNavItem key={item.label} item={item} onClose={closeMenu} />
            ))}
            <div className="mt-5 grid grid-cols-2 gap-2">
              <Link
                to={CONSULTATION_CTA.to}
                onClick={closeMenu}
                data-testid="mobile-cta-free-consultation"
                className="inline-flex items-center justify-center px-3 py-3 border border-[#C5A880] text-[#000000] text-[10px] tracking-[0.16em] uppercase font-semibold text-center leading-tight"
              >
                {CONSULTATION_CTA.label}
              </Link>
              <Link
                to={PRIMARY_CTA.to}
                onClick={closeMenu}
                data-testid="mobile-cta-contact"
                className="inline-flex items-center justify-center px-3 py-3 bg-[#000000] text-white text-[10px] tracking-[0.16em] uppercase font-semibold"
              >
                {PRIMARY_CTA.label}
                <span className="ml-1.5">→</span>
              </Link>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Navbar;
